import React, { useEffect, useState, useMemo } from 'react'
import moment from 'moment-timezone'
import { Modal, Spin, Alert } from 'antd'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  ReferenceDot,
} from 'recharts'

import { V3Transaction } from 'models/v3-transaction'
import { GetV3TransactionMeterValues, V3MeterValueEntry } from 'services/data-provider/v3-transaction'
import { V3TransactionStateBadge } from 'components/transactions/transaction-state-badge'
import { formatDurationWithTwoDigits } from 'helpers/duration'

interface MeterValuesModalProps {
  transaction: V3Transaction | null
  visible: boolean
  onClose: () => void
}

/** Loitering interval from suspendedDurations (startTime/endTime only) */
type SuspendedInterval = { startTime?: string; endTime?: string }

interface ChartPoint {
  durationSeconds: number
  durationLabel: string
  charging: number | null
  /** Green segment from connected time to first meter value; null elsewhere */
  connectedSegment: number | null
  /** Green segment from last meter value to disconnected point; null elsewhere */
  disconnectedSegment: number | null
  /** Red segment from loitering start to next point; null elsewhere */
  loiteringSegment: number | null
  /** True for the point at transaction end (disconnected) */
  isDisconnected?: boolean
  /** True for the point at loitering start (time_based only) */
  isLoiteringStart?: boolean
  /** True for status_based loitering interval boundary (keep in downsampling) */
  isLoiteringBoundary?: boolean
  timestamp?: string
}

function formatDurationAxis(seconds: number): string {
  const d = moment.duration(seconds, 'seconds')
  const h = Math.floor(d.asHours())
  const m = d.minutes()
  const s = d.seconds()
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

/** Format energy (kWh) with 4 decimal places */
function formatEnergyKwh(value: number): string {
  return value.toFixed(4)
}

export const MeterValuesModal: React.FC<MeterValuesModalProps> = ({
  transaction,
  visible,
  onClose,
}) => {
  const [meterValues, setMeterValues] = useState<V3MeterValueEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!visible || !transaction) {
      setMeterValues([])
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    GetV3TransactionMeterValues(transaction.id)
      .then(setMeterValues)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load meter values')
        setMeterValues([])
      })
      .finally(() => setLoading(false))
  }, [visible, transaction?.id])

  const { chartData, durationInfo, loiteringSegments, hasPositiveMeterValue } = useMemo(() => {
    if (!transaction) {
      return {
        chartData: [] as ChartPoint[],
        durationInfo: null,
        loiteringSegments: [] as Array<{ startSec: number; endSec: number }>,
        hasPositiveMeterValue: false,
      }
    }

    const parsedStartAt = new Date(transaction.startAt).getTime()
    const meterTimestampEntries = meterValues
      .map((mv) => ({ raw: mv.timestamp, ts: mv.timestamp ? new Date(mv.timestamp).getTime() : NaN }))
      .filter((entry) => Number.isFinite(entry.ts))
    const meterTimestamps = meterTimestampEntries.map((entry) => entry.ts)
    const earliestMeterTs = meterTimestamps.length > 0 ? Math.min(...meterTimestamps) : undefined
    const hasAnyMeterAtOrAfterStart =
      Number.isFinite(parsedStartAt) && meterTimestamps.some((ts) => ts >= parsedStartAt)
    const shouldFallbackToMeterBaseline =
      Number.isFinite(parsedStartAt) && !hasAnyMeterAtOrAfterStart && earliestMeterTs != null

    const startAt = shouldFallbackToMeterBaseline
      ? (earliestMeterTs as number)
      : Number.isFinite(parsedStartAt)
        ? parsedStartAt
        : (earliestMeterTs ?? 0)

    const startTimestamp =
      shouldFallbackToMeterBaseline
        ? meterTimestampEntries.find((entry) => entry.ts === earliestMeterTs)?.raw ?? transaction.startAt
        : transaction.startAt

    const rawPoints: ChartPoint[] = []

    // Add origin (transaction connected time)
    rawPoints.push({
      durationSeconds: 0,
      durationLabel: '0s',
      charging: 0,
      connectedSegment: 0,
      disconnectedSegment: null,
      loiteringSegment: null,
      timestamp: startTimestamp,
    })

    const sortedMeterValues = [...meterValues]
      .filter((mv) => mv.timestamp != null && (mv.charged_value != null || mv.value != null))
      .map((mv) => {
        const ts = new Date(mv.timestamp!).getTime()
        const sec = (ts - startAt) / 1000
        const energyWh = Number(mv.charged_value ?? mv.value ?? 0)
        const energyKwh = energyWh / 1000
        return { durationSeconds: sec, energyKwh, timestamp: mv.timestamp }
      })
      .filter((p) => Number.isFinite(p.durationSeconds) && Number.isFinite(p.energyKwh))
      .sort((a, b) => a.durationSeconds - b.durationSeconds)
    const hasPositiveMeterValue = sortedMeterValues.some((p) => p.energyKwh > 0)

    let lastEnergy = 0
    let lastZeroPoint: ChartPoint | null = null
    for (const p of sortedMeterValues) {
      if (p.energyKwh === 0) {
        // Keep charging at previous value so the charging line stays connected (no spike to 0)
        lastZeroPoint = {
          durationSeconds: p.durationSeconds,
          durationLabel: formatDurationAxis(p.durationSeconds),
          charging: lastEnergy,
          connectedSegment: null,
          disconnectedSegment: null,
          loiteringSegment: null,
          timestamp: p.timestamp,
        }
        continue
      }
      lastEnergy = p.energyKwh
      if (lastZeroPoint) {
        rawPoints.push(lastZeroPoint)
        lastZeroPoint = null
      }
      rawPoints.push({
        durationSeconds: p.durationSeconds,
        durationLabel: formatDurationAxis(p.durationSeconds),
        charging: p.energyKwh,
        connectedSegment: null,
        disconnectedSegment: null,
        loiteringSegment: null,
        timestamp: p.timestamp,
      })
    }
    if (lastZeroPoint) rawPoints.push(lastZeroPoint)

    // If last meter value is less than previous, hide it (e.g. slight drop in final reading)
    if (rawPoints.length >= 2) {
      const lastCharging = rawPoints[rawPoints.length - 1].charging
      const prevCharging = rawPoints[rawPoints.length - 2].charging
      if (
        lastCharging != null &&
        prevCharging != null &&
        lastCharging < prevCharging
      ) {
        rawPoints.pop()
        lastEnergy = prevCharging
      }
    }

    // Loitering: no_penalty = don't display; status_based = suspendedDurations; time_based = loiteringStartTime to endAt
    const loiteringStatus = transaction.loiteringStatus
    const loiteringStartTime = transaction.loiteringStartTime
    const endAt = transaction.endAt
    const suspendedDurations = (transaction.suspendedDurations ?? []) as SuspendedInterval[]

    // status_based: add boundary points for each suspended interval (merge with existing point at same time to avoid spike)
    if (loiteringStatus === 'status_based' && suspendedDurations.length > 0) {
      const findAt = (t: number) => rawPoints.find((p) => Math.abs(p.durationSeconds - t) < 1)
      for (const interval of suspendedDurations) {
        const start = interval.startTime
        const end = interval.endTime
        if (!start || !end) continue
        const startSec = (new Date(start).getTime() - startAt) / 1000
        const endSec = (new Date(end).getTime() - startAt) / 1000
        const existingStart = findAt(startSec)
        if (existingStart) {
          existingStart.isLoiteringBoundary = true
          existingStart.isLoiteringStart = true
        } else {
          rawPoints.push({
            durationSeconds: startSec,
            durationLabel: formatDurationAxis(startSec),
            charging: lastEnergy,
            connectedSegment: null,
            disconnectedSegment: null,
            loiteringSegment: null,
            isLoiteringBoundary: true,
            isLoiteringStart: true,
            timestamp: start,
          })
        }
        const existingEnd = findAt(endSec)
        if (existingEnd) {
          existingEnd.isLoiteringBoundary = true
        } else {
          rawPoints.push({
            durationSeconds: endSec,
            durationLabel: formatDurationAxis(endSec),
            charging: lastEnergy,
            connectedSegment: null,
            disconnectedSegment: null,
            loiteringSegment: null,
            isLoiteringBoundary: true,
            timestamp: end,
          })
        }
      }
    }

    // time_based: add loitering start point when we have both start and end
    const drawLoiteringOnChart =
      loiteringStatus === 'time_based' && !!loiteringStartTime && !!endAt
    if (drawLoiteringOnChart) {
      const loiterStartSec = (new Date(loiteringStartTime!).getTime() - startAt) / 1000
      const existingAtLoiter = rawPoints.find(
        (p) => Math.abs(p.durationSeconds - loiterStartSec) < 1
      )
      if (existingAtLoiter) {
        existingAtLoiter.isLoiteringStart = true
        // Keep existing charging so the line doesn't spike (don't overwrite with lastEnergy)
      } else {
        rawPoints.push({
          durationSeconds: loiterStartSec,
          durationLabel: formatDurationAxis(loiterStartSec),
          charging: lastEnergy,
          connectedSegment: null,
          disconnectedSegment: null,
          loiteringSegment: null,
          isLoiteringStart: true,
          timestamp: loiteringStartTime!,
        })
      }
    }

    // Disconnected point: same energy as last meter value, at transaction end time
    if (transaction.endAt) {
      const endDurationSec = (new Date(transaction.endAt).getTime() - startAt) / 1000
      const lastPoint = rawPoints[rawPoints.length - 1]
      if (!lastPoint || endDurationSec > lastPoint.durationSeconds) {
        rawPoints.push({
          durationSeconds: endDurationSec,
          durationLabel: formatDurationAxis(endDurationSec),
          charging: lastEnergy,
          connectedSegment: null,
          isDisconnected: true,
          disconnectedSegment: null,
          loiteringSegment: null,
          timestamp: transaction.endAt,
        })
      }
    }

    rawPoints.sort((a, b) => a.durationSeconds - b.durationSeconds)

    const MAX_CHART_POINTS = 70
    const lastIdx = rawPoints.length - 1
    const loiteringStartIdx = rawPoints.findIndex((p) => p.isLoiteringStart)

    let points: ChartPoint[]
    if (rawPoints.length <= MAX_CHART_POINTS) {
      points = rawPoints
    } else {
      const keyIndices = new Set<number>([0])
      if (rawPoints.length > 1) keyIndices.add(1)
      keyIndices.add(lastIdx)
      if (lastIdx >= 1 && rawPoints[lastIdx].isDisconnected) keyIndices.add(lastIdx - 1)
      if (loiteringStartIdx >= 0) {
        keyIndices.add(loiteringStartIdx)
        if (loiteringStartIdx + 1 < rawPoints.length) keyIndices.add(loiteringStartIdx + 1)
      }
      rawPoints.forEach((p, i) => {
        if (p.isLoiteringBoundary) keyIndices.add(i)
      })
      const keyList = Array.from(keyIndices).sort((a, b) => a - b)
      const otherIndices = Array.from({ length: rawPoints.length }, (_, i) => i).filter((i) => !keyIndices.has(i))
      const slotsNeeded = MAX_CHART_POINTS - keyList.length
      const sampledIndices =
        otherIndices.length <= slotsNeeded || slotsNeeded <= 0
          ? otherIndices
          : (() => {
              const out: number[] = []
              if (slotsNeeded === 1) {
                out.push(otherIndices[Math.floor(otherIndices.length / 2)])
              } else {
                for (let k = 0; k < slotsNeeded; k++) {
                  const pos = Math.floor((k * (otherIndices.length - 1)) / (slotsNeeded - 1))
                  out.push(otherIndices[pos])
                }
              }
              return out
            })()
      const allIndices = [...keyList, ...sampledIndices].sort((a, b) => a - b)
      points = allIndices.map((i) => rawPoints[i]).sort((a, b) => a.durationSeconds - b.durationSeconds)
    }

    // Green segment: from origin (index 0) to first meter value (index 1)
    for (let i = 0; i < points.length; i++) {
      points[i].connectedSegment = i <= 1 ? (points[i].charging ?? 0) : null
    }

    // Fake point energy at loitering start/boundary: interpolate by duration so line stays smooth (e.g. fake at 2 between 1 and 7 is closer to prev than to next)
    for (let i = 0; i < points.length; i++) {
      if (points[i].isLoiteringStart || points[i].isLoiteringBoundary) {
        const prev = i > 0 ? points[i - 1].charging : null
        const next = i < points.length - 1 ? points[i + 1].charging : null
        if (prev != null && next != null) {
          const tPrev = points[i - 1].durationSeconds
          const tFake = points[i].durationSeconds
          const tNext = points[i + 1].durationSeconds
          const span = tNext - tPrev
          const ratio = span > 0 ? (tFake - tPrev) / span : 0.5
          points[i].charging = prev + (next - prev) * ratio
        } else if (prev != null) points[i].charging = prev
        else if (next != null) points[i].charging = next
      }
    }

    const pointsLastIdx = points.length - 1
    const pointsLoiteringStartIdx = points.findIndex((p) => p.isLoiteringStart)
    const pointsDisconnectedIdx = points.findIndex((p) => p.isDisconnected)

    // Last charging point: last point with charging value that is not loitering start or disconnected
    let lastChargingIdx = -1
    for (let i = pointsLastIdx; i >= 0; i--) {
      if (points[i].charging != null && !points[i].isLoiteringStart && !points[i].isDisconnected) {
        lastChargingIdx = i
        break
      }
    }

    // Loitering drawn via ReferenceLine (separate from data) so charging line stays connected and no spike
    const loiteringSegments: Array<{ startSec: number; endSec: number }> = []
    if (loiteringStatus === 'time_based' && loiteringStartTime && endAt) {
      loiteringSegments.push({
        startSec: (new Date(loiteringStartTime).getTime() - startAt) / 1000,
        endSec: (new Date(endAt).getTime() - startAt) / 1000,
      })
    } else if (loiteringStatus === 'status_based' && suspendedDurations.length > 0) {
      for (const interval of suspendedDurations) {
        const start = interval.startTime
        const end = interval.endTime
        if (start && end) {
          loiteringSegments.push({
            startSec: (new Date(start).getTime() - startAt) / 1000,
            endSec: (new Date(end).getTime() - startAt) / 1000,
          })
        }
      }
    }

    if (loiteringSegments.length === 0 && pointsLastIdx >= 1 && points[pointsLastIdx].isDisconnected) {
      const energy = points[pointsLastIdx].charging ?? points[pointsLastIdx - 1].charging
      if (energy != null) {
        points[pointsLastIdx].disconnectedSegment = energy
        points[pointsLastIdx - 1].disconnectedSegment = energy
      }
    }

    const connectionEnd = transaction.endAt
      ? new Date(transaction.endAt).getTime()
      : points.length > 0
        ? startAt + points[points.length - 1].durationSeconds * 1000
        : startAt + (transaction.duration ?? 0) * 1000
    const connectionSec = (connectionEnd - startAt) / 1000

    // Charging duration: use record (currentChargingDuration) so it matches the table/panel, not computed from meter values
    const chargingSec =
      transaction.currentChargingDuration != null
        ? transaction.currentChargingDuration
        : transaction.chargeStartAt && transaction.chargeEndAt
          ? (new Date(transaction.chargeEndAt).getTime() - new Date(transaction.chargeStartAt).getTime()) / 1000
          : sortedMeterValues.length >= 2
            ? sortedMeterValues[sortedMeterValues.length - 1].durationSeconds - sortedMeterValues[0].durationSeconds
            : 0

    // Loitering duration: use same field as transaction panel (financial.loiteringDuration) so they match; fallback to computed from status/intervals
    let loiteringSec = 0
    if (transaction.financial?.loiteringDuration != null && transaction.financial.loiteringDuration >= 0) {
      loiteringSec = transaction.financial.loiteringDuration
    } else {
      if (loiteringStatus === 'status_based') {
        const suspended = (transaction.suspendedDurations ?? []) as SuspendedInterval[]
        for (const interval of suspended) {
          const start = interval.startTime
          const end = interval.endTime
          if (start && end) {
            loiteringSec += (new Date(end).getTime() - new Date(start).getTime()) / 1000
          }
        }
      } else if (loiteringStatus === 'time_based' && loiteringStartTime && endAt) {
        loiteringSec =
          (new Date(endAt).getTime() - new Date(loiteringStartTime).getTime()) / 1000
      }
    }

    const durationInfo = {
      connection: connectionSec,
      charging: chargingSec,
      loitering: loiteringSec,
    }

    return {
      chartData: points.filter((p) => p.durationSeconds >= 0),
      durationInfo,
      loiteringSegments,
      hasPositiveMeterValue,
    }
  }, [transaction, meterValues])

  return (
    <Modal
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span>Transaction {transaction?.id ?? ''} – Energy vs Duration</span>
          {transaction && <V3TransactionStateBadge transaction={transaction} />}
        </span>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnClose
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}
      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} />
      )}
      {!loading && !error && transaction && (
        <>
          {chartData.length > 0 && hasPositiveMeterValue ? (
            <div style={{ marginBottom: 24 }}>
              <ResponsiveContainer width="100%" height={440}>
                <LineChart
                  data={chartData}
                  margin={{ top: 16, right: 30, left: 24, bottom: 24 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="durationSeconds"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={(sec) => formatDurationAxis(sec)}
                    label={{
                      value: 'Duration',
                      position: 'insideBottom',
                      offset: -8,
                    }}
                  />
                  <YAxis
                    width={60}
                    label={{
                      value: 'Energy (kWh)',
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                      dx: -22,
                    }}
                    domain={[(dataMin: number) => Math.min(0, dataMin), (dataMax: number) => Math.max(dataMax ?? 0, 0.001)]}
                    tickFormatter={(v) => formatEnergyKwh(Number(v))}
                    allowDecimals
                    tickCount={6}
                  />
                  <Tooltip
                    cursor={{ stroke: '#d9d9d9', strokeWidth: 1, strokeDasharray: '3 3' }}
                    labelFormatter={(label) => `Duration: ${formatDurationAxis(Number(label))}`}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length || label == null) return null
                      const chargingPayload = payload.find((p) => p.dataKey === 'charging')
                      const energyValue = chargingPayload?.value != null
                        ? Number(chargingPayload.value)
                        : chartData.find((p) => Math.abs(p.durationSeconds - Number(label)) < 0.01)?.charging
                      const point = chartData.find((p) => Math.abs(p.durationSeconds - Number(label)) < 0.01)
                      const isConnectedTime = point?.durationSeconds === 0
                      // Only show Loitering when hovering an actual loitering point (start or boundary/end), not when hovering the charging line in between
                      const isLoiteringSpot = point?.isLoiteringStart === true || point?.isLoiteringBoundary === true
                      const ts = point?.timestamp
                        ? moment(point.timestamp).tz(transaction.timezone || 'UTC').format('MMM D, hh:mm:ss A z')
                        : ''
                      return (
                        <div
                          style={{
                            background: 'white',
                            border: '1px solid #e8e8e8',
                            borderRadius: 4,
                            padding: '8px 12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          }}
                        >
                          <div style={{ marginBottom: 4 }}>
                            {isLoiteringSpot ? (
                              <span style={{ color: '#ff4d4f' }}>Loitering</span>
                            ) : isConnectedTime ? (
                              <span style={{ color: '#52c41a' }}>Connected</span>
                            ) : point?.isDisconnected ? (
                              <span style={{ color: '#52c41a' }}>Disconnected</span>
                            ) : (
                              payload.find((p) => p.dataKey === 'charging') && (
                                <span style={{ color: '#1890ff' }}>Charging</span>
                              )
                            )}
                          </div>
                          <div>Duration: {formatDurationAxis(Number(label))}</div>
                          {!isLoiteringSpot && (
                            <div>
                              Energy: {energyValue != null ? `${formatEnergyKwh(energyValue)} kWh` : '-'}
                            </div>
                          )}
                          {ts && <div style={{ fontSize: 12, color: '#666' }}>{ts}</div>}
                        </div>
                      )
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="charging"
                    name="Charging"
                    stroke="#1890ff"
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="connectedSegment"
                    name="Connected"
                    stroke="#52c41a"
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="disconnectedSegment"
                    name="Disconnected"
                    stroke="#52c41a"
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  {/* Loitering: one ReferenceLine per segment; inset line so adjacent segments show a gap (not combined as one) */}
                  {loiteringSegments?.map((seg, idx) => {
                    const span = seg.endSec - seg.startSec
                    const inset = span > 2 ? 1 : span / 4
                    const x1 = seg.startSec + inset
                    const x2 = seg.endSec - inset
                    if (x2 <= x1) return <React.Fragment key={idx} />
                    return (
                      <ReferenceLine
                        key={idx}
                        segment={[{ x: x1, y: 0 }, { x: x2, y: 0 }]}
                        stroke="#ff4d4f"
                        strokeWidth={2}
                      />
                    )
                  })}
                  {/* Start and end dot for each loitering segment */}
                  {loiteringSegments?.flatMap((seg, idx) => [
                    <ReferenceDot key={`loiter-start-${idx}`} x={seg.startSec} y={0} r={4} fill="#ff4d4f" stroke="#fff" strokeWidth={1.5} />,
                    <ReferenceDot key={`loiter-end-${idx}`} x={seg.endSec} y={0} r={4} fill="#ff4d4f" stroke="#fff" strokeWidth={1.5} />,
                  ])}
                  {/* Dots layer: render last so dots always appear on top of lines; focus (activeDot) uses same bigger size as loitering */}
                  <Line
                    type="monotone"
                    dataKey="charging"
                    stroke="transparent"
                    strokeWidth={0}
                    dot={({ cx, cy, index, payload }) => {
                      if (cx == null || cy == null) return <circle cx={0} cy={0} r={0} />
                      const isConnected = index === 0
                      const isDisconnected = payload.isDisconnected === true
                      const isFakePoint = payload.isLoiteringStart === true || payload.isLoiteringBoundary === true
                      if (isFakePoint) return <circle cx={0} cy={0} r={0} />
                      const isCharging = payload.charging != null && index > 0 && !isDisconnected
                      if (!isConnected && !isCharging && !isDisconnected) return <circle cx={cx} cy={cy} r={0} />
                      const fill = isConnected ? '#52c41a' : isDisconnected ? '#52c41a' : '#1890ff'
                      return <circle cx={cx} cy={cy} r={4} fill={fill} stroke="#fff" strokeWidth={1.5} />
                    }}
                    activeDot={({ cx, cy, index, payload }) => {
                      if (cx == null || cy == null) return <circle cx={0} cy={0} r={0} />
                      const isConnected = index === 0
                      const isDisconnected = payload.isDisconnected === true
                      const isFakePoint = payload.isLoiteringStart === true || payload.isLoiteringBoundary === true
                      if (isFakePoint) return <circle cx={cx} cy={cy} r={5} fill="#ff4d4f" stroke="#fff" strokeWidth={2} />
                      const isCharging = payload.charging != null && index > 0 && !isDisconnected
                      if (!isConnected && !isCharging && !isDisconnected) return <circle cx={0} cy={0} r={0} />
                      const fill = isConnected ? '#52c41a' : isDisconnected ? '#52c41a' : '#1890ff'
                      return <circle cx={cx} cy={cy} r={5} fill={fill} stroke="#fff" strokeWidth={2} />
                    }}
                    connectNulls={false}
                    isAnimationActive={false}
                    legendType="none"
                  />
                  </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ padding: 24, color: '#666' }}>
              No meter values for this transaction.
            </div>
          )}

          {durationInfo && hasPositiveMeterValue && (
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <div style={{ marginBottom: 12, fontWeight: 600 }}>Duration Information</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                <DurationCard
                  label="Connection"
                  durationSeconds={durationInfo.connection}
                  color="#52c41a"
                />
                <DurationCard
                  label="Charging"
                  durationSeconds={durationInfo.charging}
                  color="#1890ff"
                />
                {durationInfo.loitering > 0 && (
                  <DurationCard
                    label="Loitering"
                    durationSeconds={durationInfo.loitering}
                    color="#ff4d4f"
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  )
}

function DurationCard({
  label,
  durationSeconds,
  color,
}: {
  label: string
  durationSeconds: number
  color: string
}) {
  return (
    <div
      style={{
        border: `1px solid #e8e8e8`,
        borderRadius: 4,
        overflow: 'hidden',
        minWidth: 120,
      }}
    >
      <div style={{ height: 4, backgroundColor: color }} />
      <div style={{ padding: '12px 16px' }}>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{label}</div>
        <div style={{ fontWeight: 600 }}>{formatDurationWithTwoDigits(durationSeconds)}</div>
      </div>
    </div>
  )
}
